import axios from 'axios';
import { logger } from './logger';

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    // If running on dev domain or local environment, strictly route to DEV worker API
    if (host.includes('dev') || host.includes('localhost') || host === '127.0.0.1') {
      return 'https://schema-graphos-api-dev.gilson-fonsaca.workers.dev';
    }
  }
  return import.meta.env.VITE_API_URL || 'https://schema-graphos-api.gilson-fonsaca.workers.dev';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // Increased to 60s for large models
  maxContentLength: 50 * 1024 * 1024, // 50MB
  maxBodyLength: 50 * 1024 * 1024, // 50MB
});

// Token getter function (will be set by AuthProvider)
let getTokenFunction = null;

// Called by AuthProvider when a 401 is received and no valid session exists
let onUnauthorizedCallback = null;

// Function to set token getter (called from AuthProvider)
export const setTokenGetter = (fn) => {
  getTokenFunction = fn;
};

// Function to register the 401 handler (called from AuthProvider)
export const setUnauthorizedHandler = (fn) => {
  onUnauthorizedCallback = fn;
};

// Request interceptor - adds auth token
api.interceptors.request.use(
  async (config) => {
    // Get fresh token if available
    if (getTokenFunction) {
      try {
        const token = await getTokenFunction();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        logger.error('Failed to get authentication token:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      logger.warn('Authentication required. Token may be expired.');
      if (onUnauthorizedCallback) {
        onUnauthorizedCallback();
      }
    } else if (error.response?.status === 403) {
      logger.warn('Access forbidden. Insufficient permissions.');
    }

    logger.error('API Error:', message);
    return Promise.reject(new Error(message));
  }
);

// Model API functions
export const modelApi = {
  // Get all models
  getAll: async (params = {}) => {
    return await api.get('/models', { params });
  },
  
  // Get model by ID
  getById: async (id) => {
    return await api.get(`/models/${id}`);
  },
  
  // Create new model
  create: async (modelData) => {
    return await api.post('/models', modelData);
  },
  
  // Update model
  update: async (id, modelData) => {
    return await api.put(`/models/${id}`, modelData);
  },
  
  // Rename model
  rename: async (id, newName) => {
    return await api.put(`/models/${id}`, { name: newName });
  },
  
  // Delete model
  delete: async (id) => {
    return await api.delete(`/models/${id}`);
  },
  
  // Export model
  export: async (id) => {
    logger.log('API: Exporting model with ID:', id);
    const exportUrl = `${API_BASE_URL}/api/models/export/${id}`;
    logger.log('API: Request URL:', exportUrl);
    
    // Get token for export request
    const headers = {};
    if (getTokenFunction) {
      const token = await getTokenFunction();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }
    
    const response = await axios.get(exportUrl, {
      responseType: 'blob',
      timeout: 30000,
      headers
    });
    
    logger.log('API: Export response received:', response);
    return response.data;
  },
  
  // Import model
  import: async (modelData) => {
    return await api.post('/models/import', modelData);
  },
  
  // Get statistics
  getStats: async () => {
    return await api.get('/models/stats/overview');
  }
};

// Domain API functions
export const domainApi = {
  // Get all domains
  getAll: async (params = {}) => {
    return await api.get('/domains', { params });
  },
  
  // Get domain by ID
  getById: async (id) => {
    return await api.get(`/domains/${id}`);
  },
  
  // Create new domain
  create: async (domainData) => {
    return await api.post('/domains', domainData);
  },
  
  // Update domain (rename)
  update: async (id, domainData) => {
    return await api.put(`/domains/${id}`, domainData);
  },
  
  // Rename domain
  rename: async (id, newName) => {
    return await api.put(`/domains/${id}`, { name: newName });
  },
  
  // Delete domain
  delete: async (id) => {
    return await api.delete(`/domains/${id}`);
  }
};

// Version API functions
export const versionApi = {
  // Get all versions for a model
  getByModelId: async (modelId) => {
    return await api.get(`/versions/model/${modelId}`);
  },
  
  // Get specific version with full snapshot
  getById: async (versionId) => {
    return await api.get(`/versions/${versionId}`);
  },
  
  // Create new version
  create: async (versionData) => {
    return await api.post('/versions', versionData);
  },
  
  // Delete version
  delete: async (versionId) => {
    return await api.delete(`/versions/${versionId}`);
  },
  
  // Restore version
  restore: async (versionId, restoreData) => {
    return await api.post(`/versions/${versionId}/restore`, restoreData);
  }
};

// User-Defined Types API functions
export const userTypeApi = {
  getAll: () => api.get('/user-types'),
  getById: (id) => api.get(`/user-types/${id}`),
  getUsage: (id) => api.get(`/user-types/${id}/usage`),
  create: (data) => api.post('/user-types', data),
  update: (id, data) => api.put(`/user-types/${id}`, data),
  delete: (id) => api.delete(`/user-types/${id}`),
};

// User API functions
export const userApi = {
  // Change password
  changePassword: async (currentPassword, newPassword) => {
    return await api.post('/user/change-password', {
      currentPassword,
      newPassword
    });
  },
  // Delete user account permanently
  deleteAccount: async () => {
    return await api.delete('/user/account');
  }
};

// AI Assistant API
export const aiApi = {
  getSettings:  ()                       => api.get('/ai/settings'),
  saveSettings: (payload)                => api.post('/ai/settings', payload),
  deleteSettings: ()                     => api.delete('/ai/settings'),
  complete:     (payload)                => api.post('/ai/complete', payload),
  getHistory:   (modelId)                => api.get(`/ai/history/${modelId}`),
  clearHistory: (modelId)                => api.delete(`/ai/history/${modelId}`),
  markApplied:  (modelId, messageIndex)  =>
    api.patch(`/ai/history/${modelId}/messages/${messageIndex}`, { appliedToModel: true }),
};

// User Messages API
export const userMessagesApi = {
  // Fetch active messages not yet confirmed by the current user
  getPending: () => api.get('/user-messages/pending'),
  // Confirm reading a message
  confirmRead: (messageId) => api.post(`/user-messages/${messageId}/read`)
};

export default api;
